'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  format,
  addDays,
  startOfDay,
  getDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from 'date-fns'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import CustomCalendar from '@/components/dev/customCalendar'
import { CheckCircle } from 'lucide-react'
import type {
  BookingData,
  BookingDurationMinutes,
  BookingSetter,
} from '@/types/booking'
import { useQuery } from 'convex/react'
import { api } from '@/convex/_generated/api'
import StepActions from '@/components/booking/step-actions'
import {
  isFlowBlockedOnDate,
  getBlockedRangesForFlow,
} from '@/config/booking-restrictions'
import {
  getBookingAvailabilityForDoctor,
  type BookingTimeWindow,
} from '@/config/booking-availability'

/* ────────────────────────────────────────────── */
/*  Types & helpers                               */
/* ────────────────────────────────────────────── */

export type DateTimeMode = 'appointment' | 'assessment'

type BlackoutItem = string | { start: string; end: string }

type SlotOption = {
  value: string
  label: string
  startMinutes: number
  endMinutes: number
}

type TimeWindow = BookingTimeWindow

type MinuteWindow = {
  start: number
  end: number
}

type LabeledMinuteWindow = MinuteWindow & {
  label: string | undefined
}

type OccupiedWindow = {
  start?: string | null
  end?: string | null
}

const FALLBACK_DURATIONS: readonly BookingDurationMinutes[] = [30, 60]

const TIMELINE_STEP_MINUTES = 15
const PIXELS_PER_MINUTE = 1.55
const MIN_TIMELINE_HEIGHT = 380
const TIMELINE_TOP_PADDING = 24
const TIMELINE_BOTTOM_PADDING = 16

const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10))
  return h * 60 + (m || 0)
}

const safeTimeToMinutes = (
  time: string | undefined,
  fallback: number,
): number => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return fallback
  return hhmmToMinutes(time)
}

const minutesToDate = (mins: number): Date => {
  const d = new Date(2000, 0, 1)
  d.setHours(0, 0, 0, 0)
  d.setMinutes(mins)
  return d
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))

const minutesToValue = (s: number, e: number) =>
  `${pad2(Math.floor(s / 60))}_${pad2(s % 60)}_${pad2(
    Math.floor(e / 60),
  )}_${pad2(e % 60)}`

const rangeLabel = (s: number, e: number) =>
  `${format(minutesToDate(s), 'h:mm a')} – ${format(
    minutesToDate(e),
    'h:mm a',
  )}`

const timelineLabel = (mins: number) => format(minutesToDate(mins), 'h:mm a')

function timeWindowsToMinuteWindows(
  windows: ReadonlyArray<TimeWindow>,
): MinuteWindow[] {
  return windows
    .map((w) => ({
      start: Math.min(hhmmToMinutes(w.start), hhmmToMinutes(w.end)),
      end: Math.max(hhmmToMinutes(w.start), hhmmToMinutes(w.end)),
    }))
    .sort((a, b) => a.start - b.start)
}

function timeWindowsToLabeledMinuteWindows(
  windows: ReadonlyArray<TimeWindow>,
): LabeledMinuteWindow[] {
  return windows
    .map((w) => ({
      start: Math.min(hhmmToMinutes(w.start), hhmmToMinutes(w.end)),
      end: Math.max(hhmmToMinutes(w.start), hhmmToMinutes(w.end)),
      label: w.label,
    }))
    .sort((a, b) => a.start - b.start)
}

function occupiedWindowToMinutes(window: OccupiedWindow) {
  if (!window.start || !window.end) return null
  if (!/^\d{2}:\d{2}$/.test(window.start)) return null
  if (!/^\d{2}:\d{2}$/.test(window.end)) return null

  const start = hhmmToMinutes(window.start)
  const end = hhmmToMinutes(window.end)

  if (start >= end) return null

  return { start, end }
}

function rangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
) {
  return Math.max(aStart, bStart) < Math.min(aEnd, bEnd)
}

function isInsideAnyWindow(
  start: number,
  end: number,
  windows: ReadonlyArray<MinuteWindow>,
) {
  return windows.some((window) => start >= window.start && end <= window.end)
}

function clipWindowToTimeline(
  window: MinuteWindow,
  timelineStart: number,
  timelineEnd: number,
): MinuteWindow | null {
  const start = Math.max(window.start, timelineStart)
  const end = Math.min(window.end, timelineEnd)

  if (start >= end) return null

  return { start, end }
}

function generateCandidateSlots({
  scheduleWindows,
  blockedWindows,
  durationMinutes,
  isTodaySelected,
  nowMinutes,
}: {
  scheduleWindows: ReadonlyArray<MinuteWindow>
  blockedWindows: ReadonlyArray<MinuteWindow>
  durationMinutes: BookingDurationMinutes
  isTodaySelected: boolean
  nowMinutes: number
}): SlotOption[] {
  const out: SlotOption[] = []

  for (const window of scheduleWindows) {
    for (
      let start = window.start;
      start + durationMinutes <= window.end;
      start += TIMELINE_STEP_MINUTES
    ) {
      const end = start + durationMinutes

      if (isTodaySelected && start <= nowMinutes) continue
      if (!isInsideAnyWindow(start, end, scheduleWindows)) continue

      const overlapsBlocked = blockedWindows.some((blocked) =>
        rangesOverlap(start, end, blocked.start, blocked.end),
      )

      if (overlapsBlocked) continue

      out.push({
        value: minutesToValue(start, end),
        label: rangeLabel(start, end),
        startMinutes: start,
        endMinutes: end,
      })
    }
  }

  return out
}

function expandDates(items: BlackoutItem[]): Set<string> {
  const out = new Set<string>()

  for (const it of items) {
    if (typeof it === 'string') {
      out.add(it)
    } else {
      let d = parseISO(it.start)
      const end = parseISO(it.end)

      while (d <= end) {
        out.add(format(d, 'yyyy-MM-dd'))
        d = addDays(d, 1)
      }
    }
  }

  return out
}

function slotValueToMinuteWindow(value: string): MinuteWindow | null {
  const match = value.match(/^(\d{2})_(\d{2})_(\d{2})_(\d{2})$/)

  if (!match) return null

  const [, sh, sm, eh, em] = match
  const start = Number(sh) * 60 + Number(sm)
  const end = Number(eh) * 60 + Number(em)

  if (start >= end) return null

  return { start, end }
}

/* statuses considered blocking for occupancy calc */
const BLOCKING_STATUSES = ['booked', 'booking confirmed'] as const

/* ────────────────────────────────────────────── */
/*  Component                                     */
/* ────────────────────────────────────────────── */

interface Props {
  mode: DateTimeMode
  bookingData: BookingData
  setBookingData: BookingSetter
  selectedDoctorName?: string
  onNext: () => void
  onPrevious: () => void
}

const StepDateTime = ({
  mode,
  bookingData,
  setBookingData,
  selectedDoctorName,
  onNext,
  onPrevious,
}: Props) => {
  const isAppointment = mode === 'appointment'
  const dateChosen = !!bookingData.bookingDate

  const [currentTime, setCurrentTime] = useState(() => new Date())

  useEffect(() => {
    const id = window.setInterval(() => {
      setCurrentTime(new Date())
    }, 60 * 1000)

    return () => window.clearInterval(id)
  }, [])

  const doctorID = bookingData.doctorID ?? ''
  const doctorName = bookingData.doctorName ?? selectedDoctorName ?? ''
  const dateStr = bookingData.bookingDate
    ? format(bookingData.bookingDate, 'yyyy-MM-dd')
    : undefined

  const availability = useMemo(
    () =>
      getBookingAvailabilityForDoctor({
        doctorID,
        doctorName,
        flowType: bookingData.flowType,
      }),
    [bookingData.flowType, doctorID, doctorName],
  )

  const allowedDurations = useMemo<readonly BookingDurationMinutes[]>(() => {
    return availability.allowedDurations.length
      ? availability.allowedDurations
      : FALLBACK_DURATIONS
  }, [availability.allowedDurations])

  const defaultDuration = useMemo<BookingDurationMinutes>(() => {
    if (allowedDurations.includes(availability.defaultDuration)) {
      return availability.defaultDuration
    }

    return allowedDurations[0] ?? 60
  }, [allowedDurations, availability.defaultDuration])

  const selectedDuration = useMemo<BookingDurationMinutes>(() => {
    const current = bookingData.durationMinutes

    if (current && allowedDurations.includes(current)) {
      return current
    }

    return defaultDuration
  }, [allowedDurations, bookingData.durationMinutes, defaultDuration])

  const scheduleWindows = availability.bookableWindows
  const configuredBreakWindows = availability.breakWindows

  const scheduleMinuteWindows = useMemo(
    () => timeWindowsToMinuteWindows(scheduleWindows),
    [scheduleWindows],
  )

  const breakMinuteWindows = useMemo(
    () => timeWindowsToLabeledMinuteWindows(configuredBreakWindows),
    [configuredBreakWindows],
  )

  const timelineStart = safeTimeToMinutes(
    availability.timelineStart,
    scheduleMinuteWindows[0]?.start ?? 9 * 60 + 30,
  )

  const timelineEnd = safeTimeToMinutes(
    availability.timelineEnd,
    scheduleMinuteWindows[scheduleMinuteWindows.length - 1]?.end ?? 17 * 60,
  )

  const timelineTotalMinutes = Math.max(1, timelineEnd - timelineStart)
  const timelineHeight = Math.max(
    MIN_TIMELINE_HEIGHT,
    timelineTotalMinutes * PIXELS_PER_MINUTE +
      TIMELINE_TOP_PADDING +
      TIMELINE_BOTTOM_PADDING,
  )

  const minuteToTop = (minute: number) =>
    TIMELINE_TOP_PADDING + (minute - timelineStart) * PIXELS_PER_MINUTE

  const minuteToHeight = (start: number, end: number) =>
    Math.max(22, (end - start) * PIXELS_PER_MINUTE)

  /* Occupancy only for appointments */
  const occupiedWindows = useQuery(
    api.bookings.occupiedWindowsForDoctorDate,
    isAppointment && dateStr && (doctorID || doctorName)
      ? {
          doctorID: doctorID || undefined,
          doctorName: doctorName || undefined,
          bookingDate: dateStr,
          statuses: [...BLOCKING_STATUSES],
          userFlowType: bookingData.flowType || undefined,
        }
      : 'skip',
  ) as OccupiedWindow[] | undefined

  const todayYMD = useMemo(
    () => format(startOfDay(new Date()), 'yyyy-MM-dd'),
    [],
  )

  /* National holidays (DB) */
  const holidaysList = useQuery(api.nationalHolidays.list, {}) as
    | {
        start_date?: string | null
        end_date?: string | null
        occasion?: string | null
      }[]
    | undefined

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    console.groupCollapsed('[StepDateTime] holidaysList (raw from Convex)')
    console.table(
      (holidaysList ?? []).map((h) => ({
        start_date: h.start_date ?? null,
        end_date: h.end_date ?? null,
        occasion: h.occasion ?? null,
      })),
    )
    console.groupEnd()
  }, [holidaysList])

  const holidayMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!holidaysList) return map

    for (const h of holidaysList) {
      const occ = (h.occasion ?? '').trim()
      if (!occ) continue

      const sRaw = (h.start_date || h.end_date || '').trim()
      if (!sRaw) continue

      const eRaw = (h.end_date || h.start_date || '').trim()
      let d = parseISO(sRaw)
      const end = eRaw ? parseISO(eRaw) : parseISO(sRaw)

      while (d <= end) {
        map.set(format(d, 'yyyy-MM-dd'), occ)
        d = addDays(d, 1)
      }
    }

    return map
  }, [holidaysList])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    const expanded = Array.from(holidayMap.entries()).map(([date, label]) => ({
      date,
      label,
    }))
    console.groupCollapsed(
      '[StepDateTime] holidays (expanded days from labeled rows)',
    )
    console.table(expanded)
    console.groupEnd()
  }, [holidayMap])

  const leaves = useQuery(
    api.leaves?.upcomingByDoctorName as any,
    isAppointment && doctorName && doctorName.trim() !== ''
      ? { doctorName, startDate: todayYMD }
      : 'skip',
  ) as
    | { start_date: string; end_date: string; reason: string; type?: string }[]
    | undefined

  const next7Days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => addDays(startOfDay(new Date()), i)),
    [],
  )

  const isInNext7 = (d: Date) =>
    next7Days.some((x) => format(x, 'yyyy-MM-dd') === format(d, 'yyyy-MM-dd'))

  const monthDays = useMemo(() => {
    const anchor = bookingData.bookingDate ?? new Date()

    return eachDayOfInterval({
      start: startOfMonth(anchor),
      end: endOfMonth(anchor),
    })
  }, [bookingData.bookingDate])

  const holidaysInMonth = useMemo(() => {
    return monthDays
      .map((d) => {
        const ymd = format(d, 'yyyy-MM-dd')
        const label = holidayMap.get(ymd)
        return label ? { date: ymd, label } : null
      })
      .filter(Boolean) as { date: string; label: string }[]
  }, [monthDays, holidayMap])

  const holidaysInMonthSet = useMemo(
    () => new Set(holidaysInMonth.map((h) => h.date)),
    [holidaysInMonth],
  )

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    console.groupCollapsed('[StepDateTime] holidaysInMonth (legend)')
    console.table(holidaysInMonth)
    console.groupEnd()
    console.log(
      '[StepDateTime] tinted days (from legend):',
      Array.from(holidaysInMonthSet.values()),
    )
  }, [holidaysInMonth, holidaysInMonthSet])

  const leaveBlockedSet = useMemo(() => {
    if (!isAppointment) return new Set<string>()

    const userFlow = (bookingData.flowType ?? '').trim().toLowerCase()

    const applicableLeaves = (leaves ?? []).filter((l) => {
      const leaveType = (l.type ?? 'both').trim().toLowerCase()

      if (leaveType === 'both') return true

      if (
        (leaveType === 'inperson' || leaveType === 'in-person') &&
        userFlow === 'in-person'
      ) {
        return true
      }

      if (leaveType === 'online' && userFlow === 'online') {
        return true
      }

      return false
    })

    const items = applicableLeaves.map((l) => ({
      start: l.start_date,
      end: l.end_date,
    }))

    return expandDates(items)
  }, [isAppointment, leaves, bookingData.flowType])

  const isDateDisabled = (d: Date) => {
    const ymd = format(d, 'yyyy-MM-dd')
    const dow = getDay(d)

    if (
      isFlowBlockedOnDate(bookingData.flowType, d, bookingData.doctorID).blocked
    ) {
      return true
    }

    if (holidayMap.has(ymd)) return true

    if (isAppointment) {
      const sunday = dow === 0
      if (leaveBlockedSet.has(ymd)) return true
      return sunday || !isInNext7(d)
    }

    const weekend = dow === 0 || dow === 6
    return weekend || !isInNext7(d)
  }

  const flowRestrictionNotice = useMemo(() => {
    const ranges = getBlockedRangesForFlow(
      bookingData.flowType,
      bookingData.doctorID,
    )

    if (ranges.length === 0) return null

    const today = format(new Date(), 'yyyy-MM-dd')
    const weekFromNow = format(addDays(new Date(), 6), 'yyyy-MM-dd')

    const activeRestrictions = ranges.filter(
      (r) => r.endDate >= today && r.startDate <= weekFromNow,
    )

    if (activeRestrictions.length === 0) return null

    return activeRestrictions[0]
  }, [bookingData.flowType, bookingData.doctorID])

  useEffect(() => {
    if (!isAppointment) return

    if (
      bookingData.durationMinutes &&
      allowedDurations.includes(bookingData.durationMinutes)
    ) {
      return
    }

    setBookingData((prev) => ({
      ...prev,
      durationMinutes: defaultDuration,
      slot: '',
    }))
  }, [
    allowedDurations,
    bookingData.durationMinutes,
    defaultDuration,
    isAppointment,
    setBookingData,
  ])

  const isLoadingSlots = Boolean(
    isAppointment &&
      dateStr &&
      (doctorID || doctorName) &&
      occupiedWindows === undefined,
  )

  const occupiedMinuteWindows = useMemo(() => {
    return (occupiedWindows ?? [])
      .map(occupiedWindowToMinutes)
      .filter((window): window is MinuteWindow => window !== null)
  }, [occupiedWindows])

  const clippedOccupiedWindows = useMemo(() => {
    return occupiedMinuteWindows
      .map((window) => clipWindowToTimeline(window, timelineStart, timelineEnd))
      .filter((window): window is MinuteWindow => window !== null)
  }, [occupiedMinuteWindows, timelineEnd, timelineStart])

  const breakWindows = useMemo<LabeledMinuteWindow[]>(() => {
    const windows: LabeledMinuteWindow[] = []

    for (const window of breakMinuteWindows) {
      const clipped = clipWindowToTimeline(window, timelineStart, timelineEnd)

      if (!clipped) continue

      windows.push({
        ...clipped,
        label: window.label,
      })
    }

    return windows
  }, [breakMinuteWindows, timelineEnd, timelineStart])

  const currentTimeMinutes =
    currentTime.getHours() * 60 + currentTime.getMinutes()

  const isSelectedDateToday = dateStr === todayYMD

  const showCurrentTimeLine =
    isSelectedDateToday &&
    currentTimeMinutes >= timelineStart &&
    currentTimeMinutes <= timelineEnd

  const candidateSlots: SlotOption[] = useMemo(() => {
    if (!isAppointment || !dateStr) return []

    return generateCandidateSlots({
      scheduleWindows: scheduleMinuteWindows,
      blockedWindows: [...occupiedMinuteWindows, ...breakMinuteWindows],
      durationMinutes: selectedDuration,
      isTodaySelected: isSelectedDateToday,
      nowMinutes: currentTimeMinutes,
    })
  }, [
    breakMinuteWindows,
    currentTimeMinutes,
    dateStr,
    isAppointment,
    isSelectedDateToday,
    occupiedMinuteWindows,
    scheduleMinuteWindows,
    selectedDuration,
  ])

  useEffect(() => {
    if (!bookingData.slot) return

    if (!candidateSlots.some((s) => s.value === bookingData.slot)) {
      setBookingData((prev) => ({ ...prev, slot: '' }))
    }
  }, [bookingData.slot, candidateSlots, setBookingData])

  useEffect(() => {
    if (!bookingData.bookingDate) return

    if (isDateDisabled(bookingData.bookingDate)) {
      setBookingData((prev) => ({
        ...prev,
        bookingDate: undefined,
        slot: '',
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorID, holidayMap, leaveBlockedSet, mode])

  const isValid = isAppointment
    ? Boolean(bookingData.bookingDate && bookingData.slot)
    : Boolean(bookingData.bookingDate)

  const labelForSlot = (value: string): string => {
    const m = value.match(/^(\d{2})_(\d{2})_(\d{2})_(\d{2})$/)

    if (m) {
      const [, sh, sm, eh, em] = m
      const s = new Date(2000, 0, 1, parseInt(sh, 10), parseInt(sm, 10))
      const e = new Date(2000, 0, 1, parseInt(eh, 10), parseInt(em, 10))
      return `${format(s, 'h:mm a')} – ${format(e, 'h:mm a')}`
    }

    if (value === 'morning') return 'Morning (9:30 AM – 1:30 PM)'
    if (value === 'afternoon') return 'Afternoon (2 PM – 5 PM)'

    return value
  }

  const selectedWindow = bookingData.slot
    ? slotValueToMinuteWindow(bookingData.slot)
    : null

  const selectedWindowClipped = selectedWindow
    ? clipWindowToTimeline(selectedWindow, timelineStart, timelineEnd)
    : null

  const selectedSlotLabel = bookingData.slot
    ? labelForSlot(bookingData.slot)
    : null

  const timeTicks = useMemo(() => {
    const ticks: number[] = []
    const firstTick = Math.ceil(timelineStart / 30) * 30

    for (let t = firstTick; t <= timelineEnd; t += 30) {
      ticks.push(t)
    }

    return ticks
  }, [timelineEnd, timelineStart])

  const gridTicks = useMemo(() => {
    const ticks: number[] = []

    for (let t = timelineStart; t <= timelineEnd; t += TIMELINE_STEP_MINUTES) {
      ticks.push(t)
    }

    return ticks
  }, [timelineEnd, timelineStart])

  const todayTimestamp = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [])

  const handleDurationChange = (duration: BookingDurationMinutes) => {
    setBookingData((prev) => ({
      ...prev,
      durationMinutes: duration,
      slot: '',
    }))
  }

  const handleSlotClick = (slot: SlotOption) => {
    setBookingData((prev) => ({
      ...prev,
      slot: slot.value,
      durationMinutes: selectedDuration,
    }))
  }

  const renderTimelinePicker = () => {
    return (
      <div className="rounded-xl border bg-white p-2.5 shadow-sm sm:p-3">
        {selectedSlotLabel && (
          <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-blue-700">
              Selected appointment
            </p>
            <p className="mt-0.5 text-sm font-semibold text-blue-950">
              {selectedSlotLabel} · {selectedDuration} min
            </p>
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-600 sm:text-xs">
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
            Selected
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-sm bg-gray-300" />
            Booked / blocked
          </span>
          {breakWindows.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-amber-100 ring-1 ring-amber-200" />
              Lunch / break
            </span>
          )}
          {showCurrentTimeLine && (
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-red-500" />
              Current time
            </span>
          )}
        </div>

        <div className="max-h-[58vh] overflow-y-auto overscroll-contain rounded-xl pr-1 sm:max-h-[620px]">
          <div
            className="relative"
            style={{
              height: timelineHeight,
            }}
          >
            {/* Time labels */}
            <div className="absolute left-0 top-0 h-full w-14 text-[10px] text-gray-500 sm:w-16 sm:text-xs">
              {timeTicks.map((tick) => (
                <div
                  key={tick}
                  className="absolute -translate-y-1/2 whitespace-nowrap"
                  style={{ top: minuteToTop(tick) }}
                >
                  {timelineLabel(tick)}
                </div>
              ))}
            </div>

            {/* Timeline surface */}
            <div className="absolute inset-y-0 left-14 right-0 overflow-hidden rounded-xl border bg-slate-50 sm:left-16">
              {/* Grid lines */}
              {gridTicks.map((tick) => {
                const isHour = tick % 60 === 0

                return (
                  <div
                    key={tick}
                    className={`absolute left-0 right-0 ${
                      isHour
                        ? 'border-t border-gray-200'
                        : 'border-t border-gray-100'
                    }`}
                    style={{ top: minuteToTop(tick) }}
                  />
                )
              })}

              {/* Current time marker - only when selected date is today */}
              {showCurrentTimeLine && (
                <div
                  className="pointer-events-none absolute left-0 right-0 z-[45] flex -translate-y-1/2 items-center"
                  style={{ top: minuteToTop(currentTimeMinutes) }}
                >
                  <span className="ml-1 shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white shadow-sm">
                    Now
                  </span>

                  <span className="h-px flex-1 bg-red-500" />

                  <span className="mr-2 shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-red-600 shadow-sm ring-1 ring-red-200">
                    {timelineLabel(currentTimeMinutes)}
                  </span>
                </div>
              )}

              {/* Lunch / break blocks */}
              {breakWindows.map((window) => (
                <div
                  key={`break-${window.start}-${window.end}`}
                  className="pointer-events-none absolute left-2 right-2 z-10 overflow-hidden rounded-lg border border-amber-200 bg-amber-50/95 px-2.5 py-1.5 text-[11px] font-medium leading-tight text-amber-800 sm:px-3 sm:py-2 sm:text-xs"
                  style={{
                    top: minuteToTop(window.start),
                    height: minuteToHeight(window.start, window.end),
                  }}
                >
                  {window.label ?? 'Break'}
                  <span className="ml-1 font-normal text-amber-700">
                    {rangeLabel(window.start, window.end)}
                  </span>
                </div>
              ))}

              {/* Booked / blocked blocks */}
              {clippedOccupiedWindows.map((window) => (
                <div
                  key={`occupied-${window.start}-${window.end}`}
                  className="pointer-events-none absolute left-2 right-2 z-20 overflow-hidden rounded-lg border border-gray-300 bg-gray-200/95 px-2.5 py-1.5 text-[11px] font-medium leading-tight text-gray-700 sm:px-3 sm:py-2 sm:text-xs"
                  style={{
                    top: minuteToTop(window.start),
                    height: minuteToHeight(window.start, window.end),
                  }}
                >
                  Booked
                  <span className="ml-1 font-normal text-gray-600">
                    {rangeLabel(window.start, window.end)}
                  </span>
                </div>
              ))}

              {/* Available start rows */}
              {candidateSlots.map((slot) => {
                const selected = bookingData.slot === slot.value

                if (selected) return null

                return (
                  <button
                    key={slot.value}
                    type="button"
                    aria-label={`Select ${slot.label}`}
                    onClick={() => handleSlotClick(slot)}
                    className="group absolute left-0 right-0 z-30 flex items-center justify-end px-2 outline-none transition-colors hover:bg-blue-100/80 focus:outline-none focus-visible:bg-blue-100/80 focus-visible:outline-none active:bg-blue-100/80"
                    style={{
                      top: minuteToTop(slot.startMinutes),
                      height: Math.max(
                        24,
                        TIMELINE_STEP_MINUTES * PIXELS_PER_MINUTE,
                      ),
                    }}
                  >
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-blue-700 opacity-0 shadow-sm ring-1 ring-blue-100 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 sm:text-xs">
                      {timelineLabel(slot.startMinutes)}
                    </span>
                  </button>
                )
              })}

              {/* Selected appointment block */}
              {selectedWindowClipped && (
                <div
                  className="pointer-events-none absolute left-2 right-2 z-50 overflow-hidden rounded-lg border border-blue-500 bg-blue-600 px-2.5 text-[11px] font-semibold leading-tight text-white shadow-lg sm:px-3 sm:text-xs"
                  style={{
                    top: minuteToTop(selectedWindowClipped.start),
                    height: minuteToHeight(
                      selectedWindowClipped.start,
                      selectedWindowClipped.end,
                    ),
                  }}
                >
                  <div className="flex h-full items-center justify-between gap-2">
                    {selectedDuration <= 15 ? (
                      <>
                        <span className="truncate">Selected</span>
                        <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white">
                          {selectedDuration} min
                        </span>
                      </>
                    ) : (
                      <div className="flex min-w-0 flex-col justify-center">
                        <span className="truncate">
                          {labelForSlot(bookingData.slot)}
                        </span>
                        <span className="truncate text-[11px] font-normal text-blue-100">
                          {selectedDuration} min appointment
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {candidateSlots.length === 0 && (
          <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 sm:text-sm">
            No available start times for this date and appointment length.
          </p>
        )}

        {candidateSlots.length > 0 && !bookingData.slot && (
          <p className="mt-3 text-xs text-gray-500 sm:text-sm">
            Tap any blank row on the timeline to choose a start time. Booked
            slots and lunch breaks are disabled.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <p className="text-center text-gray-600 text-sm sm:text-base md:text-lg px-2 sm:px-0">
        {isAppointment
          ? 'Select your preferred date, appointment length, and start time'
          : 'Select your preferred date for the assessment'}
      </p>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8">
        <div
          className={`w-full ${
            isAppointment ? 'lg:w-1/2' : 'lg:w-full'
          } bg-white rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border flex flex-col min-h-[360px] sm:min-h-[420px] md:min-h-[520px]`}
        >
          <Label className="block text-sm sm:text-base md:text-lg font-semibold mb-0.5 sm:mb-1 text-gray-800">
            📅 Available Dates{' '}
            <span className="text-xs sm:text-sm">(next 7 days)</span>
          </Label>

          <p className="mb-2 sm:mb-4 text-[10px] sm:text-xs text-muted-foreground">
            {isAppointment
              ? 'Sundays and holidays are not bookable.'
              : 'Saturdays, Sundays, and holidays are not bookable.'}
          </p>

          {flowRestrictionNotice && (
            <div className="mb-2 sm:mb-4 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 sm:px-3 sm:py-2">
              <p className="text-[10px] sm:text-xs font-medium text-amber-800">
                ⚠️{' '}
                {flowRestrictionNotice.reason ||
                  `${bookingData.flowType} bookings are restricted for some dates`}
              </p>

              <p className="text-[10px] sm:text-xs text-amber-700">
                {format(parseISO(flowRestrictionNotice.startDate), 'MMM d')} –{' '}
                {format(parseISO(flowRestrictionNotice.endDate), 'MMM d, yyyy')}
              </p>
            </div>
          )}

          <CustomCalendar
            className="h-full"
            showPastCross
            selected={bookingData.bookingDate ?? null}
            onChange={(d) => {
              setBookingData((prev) => ({
                ...prev,
                bookingDate: d ?? undefined,
                slot: '',
              }))
            }}
            minDate={startOfDay(new Date())}
            maxDate={addDays(startOfDay(new Date()), 6)}
            isDateDisabled={isDateDisabled}
            getTileClassName={(date) => {
              const ymd = format(date, 'yyyy-MM-dd')
              const d = new Date(date)
              d.setHours(0, 0, 0, 0)

              const classes: string[] = []

              if (holidaysInMonthSet.has(ymd)) classes.push('cal-holiday')

              if (d.getTime() > todayTimestamp && !isInNext7(d)) {
                classes.push('cal-future-locked')
              }

              return classes.length ? classes.join(' ') : undefined
            }}
            hideDoubleArrows
          />

          {holidaysInMonth.length > 0 && (
            <div className="mt-2 sm:mt-4 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 sm:px-3 sm:py-2">
              <p className="text-[10px] sm:text-xs font-semibold text-red-700 mb-0.5 sm:mb-1">
                Holidays this month (blocked):
              </p>

              <ul className="text-[10px] sm:text-xs text-red-700 space-y-0.5">
                {holidaysInMonth.map((h) => (
                  <li key={h.date}>
                    {format(parseISO(h.date), 'EEE, MMM d')} - {h.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {isAppointment && (
          <div className="w-full lg:w-1/2 bg-white rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border">
            <Label className="block text-sm sm:text-base md:text-lg font-semibold mb-2 text-gray-800">
              ⏰ Appointment Length
            </Label>

            <div
              className={`mb-4 grid gap-2 ${
                allowedDurations.length === 1
                  ? 'grid-cols-1'
                  : allowedDurations.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
              }`}
            >
              {allowedDurations.map((duration) => {
                const selected = selectedDuration === duration

                return (
                  <Button
                    key={duration}
                    type="button"
                    variant={selected ? 'default' : 'outline'}
                    aria-pressed={selected}
                    onClick={() => handleDurationChange(duration)}
                    className={`h-9 text-xs sm:text-sm ${
                      selected
                        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-600 hover:text-white'
                        : ''
                    }`}
                  >
                    {duration} min
                  </Button>
                )
              })}
            </div>

            <Label className="block text-sm sm:text-base md:text-lg font-semibold mb-1 text-gray-800">
              Day Timeline
            </Label>

            <p className="mb-3 text-[11px] leading-4 text-muted-foreground sm:text-xs">
              Booked slots and breaks are blocked. Tap a free start time to
              preview your appointment.
            </p>

            {!dateChosen ? (
              <p className="text-xs sm:text-sm text-muted-foreground">
                Select a date to see the day timeline.
              </p>
            ) : isLoadingSlots ? (
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                <span className="h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                Checking availability…
              </div>
            ) : (
              renderTimelinePicker()
            )}

            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> Appointment
                Summary
              </h4>

              <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm text-green-700">
                <p>
                  <strong>Doctor:</strong>{' '}
                  {doctorName?.trim() ? doctorName : '-'}
                </p>

                <p>
                  <strong>Length:</strong> {selectedDuration} min
                </p>

                <p>
                  <strong>Date:</strong>{' '}
                  {bookingData.bookingDate ? (
                    format(bookingData.bookingDate, 'EEEE, MMM d, yyyy')
                  ) : (
                    <span className="text-muted-foreground">Select a date</span>
                  )}
                </p>

                <p>
                  <strong>Time:</strong>{' '}
                  {bookingData.slot ? (
                    labelForSlot(bookingData.slot)
                  ) : (
                    <span className="text-muted-foreground">
                      Select a start time
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <StepActions
        onPrevious={onPrevious}
        onNext={onNext}
        nextDisabled={!isValid}
        previousLabel="Previous"
        nextLabel="Next Step"
      />
    </div>
  )
}

export default StepDateTime
